import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Eye, 
  MoreVertical,
  Calendar,
  Clock,
  Star,
  Phone,
  Mail,
  MapPin,
  UserCheck,
  UserX,
  AlertCircle,
  X,
  CreditCard
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../../../config/api';
import VolunteerIdCard from './VolunteerIdCard';

const ManageVolunteers = () => {
  const [volunteers, setVolunteers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Ensure volunteers is always an array
  const safeVolunteers = Array.isArray(volunteers) ? volunteers : [];
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSkills, setFilterSkills] = useState('');
  const [selectedVolunteer, setSelectedVolunteer] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showIdCardModal, setShowIdCardModal] = useState(false);
  const [idCardData, setIdCardData] = useState(null);
  const [loadingIdCard, setLoadingIdCard] = useState(false);

  // Helper function to construct profile picture URL
  const getProfilePictureUrl = (filePath) => {
    if (!filePath) return '/images/r.png';
    if (filePath.startsWith('http')) return filePath;
    return `${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:3011'}/${filePath}`;
  };

  useEffect(() => {
    // Fetch volunteers from API
    const fetchVolunteers = async () => {
      try {
        setLoading(true);
        const response = await api.get('/volunteers');
        
        // Ensure response.data is an array
        if (response && response.data && Array.isArray(response.data)) {
          setVolunteers(response.data);
        } else if (response && response.data && response.data.volunteers && Array.isArray(response.data.volunteers)) {
          // Handle case where data might be nested
          setVolunteers(response.data.volunteers);
        } else {
          setVolunteers([]);
        }
      } catch (error) {
        console.error('Error fetching volunteers:', error);
        toast.error('Failed to load volunteers');
        setVolunteers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchVolunteers();
  }, []);

  const filteredVolunteers = safeVolunteers.filter(volunteer => {
    // Ensure volunteer object has required properties
    if (!volunteer || typeof volunteer !== 'object') {
      return false;
    }
    
    const matchesSearch = (volunteer.name && volunteer.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (volunteer.email && volunteer.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (volunteer.location && volunteer.location.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = filterStatus === 'all' || volunteer.status === filterStatus;
    const matchesSkills = !filterSkills || (volunteer.skills && Array.isArray(volunteer.skills) && volunteer.skills.some(skill => 
      skill && skill.toLowerCase().includes(filterSkills.toLowerCase())
    ));
    return matchesSearch && matchesStatus && matchesSkills;
  });

  const handleStatusChange = async (volunteerId, newStatus) => {
    try {
      const response = await api.patch(`/volunteers/${volunteerId}/status`, { status: newStatus });
      
      setVolunteers(prev => prev.map(v => 
        v.id === volunteerId ? { ...v, status: newStatus } : v
      ));
      
      toast.success(`Volunteer status updated to ${newStatus}`);
    } catch (error) {
      console.error('Error updating volunteer status:', error);
      toast.error('Failed to update volunteer status');
    }
  };

  const handleDeleteVolunteer = async (volunteerId) => {
    if (window.confirm('Are you sure you want to delete this volunteer?')) {
      try {
        await api.delete(`/volunteers/${volunteerId}`);
        setVolunteers(prev => prev.filter(v => v.id !== volunteerId));
        toast.success('Volunteer deleted successfully');
      } catch (error) {
        console.error('Error deleting volunteer:', error);
        toast.error('Failed to delete volunteer');
      }
    }
  };

  const fetchVolunteerIdCardData = async (volunteerId) => {
    try {
      setLoadingIdCard(true);
      const response = await api.get(`/volunteers/${volunteerId}/id-card`);
      
      if (response.data.success) {
        setIdCardData(response.data.data);
        setShowIdCardModal(true);
      } else {
        toast.error('Failed to load ID card data');
      }
    } catch (error) {
      console.error('Error fetching ID card data:', error);
      toast.error('Failed to load ID card data');
    } finally {
      setLoadingIdCard(false);
    }
  };

  const showVolunteerDetails = (volunteer) => {
    setSelectedVolunteer(volunteer);
    setShowDetailsModal(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'pending': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'inactive': return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <UserCheck className="w-4 h-4" />;
      case 'pending': return <AlertCircle className="w-4 h-4" />;
      case 'inactive': return <UserX className="w-4 h-4" />;
      default: return <AlertCircle className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div>
             {/* Header */}
       <div className="mb-6">
         <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Manage Volunteers</h1>
         <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
           View, edit, and manage your volunteer team
         </p>
         
         
       </div>

      {/* Filters and Search */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search volunteers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
          </div>

          {/* Status Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white appearance-none"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {/* Skills Filter */}
          <div>
            <input
              type="text"
              placeholder="Filter by skills..."
              value={filterSkills}
              onChange={(e) => setFilterSkills(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
          </div>

          {/* Add Volunteer Button */}
          <div>
            <Link
              to="/ngo/dashboard/volunteers/add"
              className="w-full inline-flex justify-center items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
            >
              <Users className="mr-2" size={20} />
              Add Volunteer
            </Link>
          </div>
        </div>
      </div>

      {/* Volunteers Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Volunteer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Skills
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Activity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredVolunteers.map((volunteer) => (
                <tr key={volunteer.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                     {/* Volunteer Info */}
                   <td className="px-6 py-4 whitespace-nowrap">
                     <div className="flex items-center">
                       <img
                         src={getProfilePictureUrl(volunteer.profilePicture)}
                         alt={volunteer.firstName || 'Volunteer'}
                         className="w-10 h-10 rounded-full object-cover mr-3"
                         onError={(e) => {
                           if (e.target) {
                             e.target.src = '/images/r.png';
                           }
                         }}
                       />
                       <div>
                         <div className="text-sm font-medium text-gray-900 dark:text-white">
                           {volunteer.firstName && volunteer.lastName 
                             ? `${volunteer.firstName} ${volunteer.lastName}` 
                             : volunteer.firstName || volunteer.lastName || 'Unknown Name'
                           }
                         </div>
                         <div className="text-sm text-gray-500 dark:text-gray-400">
                           Joined {volunteer.joinDate ? new Date(volunteer.joinDate).toLocaleDateString() : 'Unknown Date'}
                         </div>
                         {volunteer.city && volunteer.state && (
                           <div className="text-xs text-gray-400 dark:text-gray-500">
                             📍 {volunteer.city}, {volunteer.state}
                           </div>
                         )}
                       </div>
                     </div>
                   </td>

                                     {/* Contact */}
                   <td className="px-6 py-4 whitespace-nowrap">
                     <div className="text-sm text-gray-900 dark:text-white">
                       <div className="flex items-center mb-1">
                         <Mail className="w-4 h-4 mr-2 text-gray-400" />
                         {volunteer.email || 'No email'}
                       </div>
                       <div className="flex items-center mb-1">
                         <Phone className="w-4 h-4 mr-2 text-gray-400" />
                         {volunteer.phone || 'No phone'}
                       </div>
                       {volunteer.dateOfBirth && (
                         <div className="text-xs text-gray-500 dark:text-gray-400">
                           🎂 {new Date(volunteer.dateOfBirth).toLocaleDateString()}
                         </div>
                       )}
                       {volunteer.gender && (
                         <div className="text-xs text-gray-500 dark:text-gray-400">
                           👤 {volunteer.gender}
                         </div>
                       )}
                     </div>
                   </td>

                                     {/* Skills */}
                   <td className="px-6 py-4 whitespace-nowrap">
                     <div className="space-y-2">
                       {/* Skills */}
                       <div>
                         <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Skills:</div>
                         <div className="flex flex-wrap gap-1">
                           {volunteer.skills && Array.isArray(volunteer.skills) && volunteer.skills.length > 0 ? (
                             <>
                               {volunteer.skills.slice(0, 3).map((skill, index) => (
                                 <span
                                   key={index}
                                   className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                                 >
                                   {skill || 'Unknown Skill'}
                                 </span>
                               ))}
                               {volunteer.skills.length > 3 && (
                                 <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                                   +{volunteer.skills.length - 3} more
                                 </span>
                               )}
                             </>
                           ) : (
                             <span className="text-xs text-gray-500">No skills listed</span>
                           )}
                         </div>
                       </div>
                       
                       {/* Interests */}
                       {volunteer.interests && Array.isArray(volunteer.interests) && volunteer.interests.length > 0 && (
                         <div>
                           <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Interests:</div>
                           <div className="flex flex-wrap gap-1">
                             {volunteer.interests.slice(0, 2).map((interest, index) => (
                               <span
                                 key={index}
                                 className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                               >
                                 {interest}
                               </span>
                             ))}
                             {volunteer.interests.length > 2 && (
                               <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                                 +{volunteer.interests.length - 2} more
                               </span>
                             )}
                           </div>
                         </div>
                       )}
                     </div>
                   </td>

                                     {/* Status */}
                   <td className="px-6 py-4 whitespace-nowrap">
                     <div className="space-y-2">
                       <div className="flex items-center">
                         <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(volunteer.status || 'pending')}`}>
                           {getStatusIcon(volunteer.status || 'pending')}
                           <span className="ml-1">{volunteer.status || 'pending'}</span>
                         </span>
                       </div>
                       
                       {/* Availability */}
                       {volunteer.availability && (
                         <div className="text-xs text-gray-500 dark:text-gray-400">
                           {volunteer.availability.weekdays && '📅 Weekdays '}
                           {volunteer.availability.weekends && '🌅 Weekends '}
                           {volunteer.availability.evenings && '🌙 Evenings '}
                           {volunteer.availability.flexible && '🔄 Flexible'}
                         </div>
                       )}
                       
                       {/* Experience */}
                       {volunteer.experience && (
                         <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-32" title={volunteer.experience}>
                           💼 {volunteer.experience.substring(0, 30)}...
                         </div>
                       )}
                     </div>
                   </td>

                                     {/* Activity */}
                   <td className="px-6 py-4 whitespace-nowrap">
                     <div className="text-sm text-gray-900 dark:text-white space-y-1">
                       <div className="flex items-center">
                         <Clock className="w-4 h-4 mr-2 text-gray-400" />
                         {volunteer.totalHours || 0} hours
                       </div>
                       <div className="flex items-center">
                         <Star className="w-4 h-4 mr-2 text-yellow-400" />
                         {volunteer.rating || 'N/A'}
                       </div>
                       {volunteer.lastActive && (
                         <div className="text-xs text-gray-500 dark:text-gray-400">
                           🕒 {new Date(volunteer.lastActive).toLocaleDateString()}
                         </div>
                       )}
                       {volunteer.motivation && (
                         <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-32" title={volunteer.motivation}>
                           💭 {volunteer.motivation.substring(0, 30)}...
                         </div>
                       )}
                     </div>
                   </td>

                  {/* Actions */}
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => showVolunteerDetails(volunteer)}
                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                        title="View Details"
                      >
                        <Eye size={16} />
                      </button>
                      <Link
                        to={`/ngo/dashboard/volunteers/edit/${volunteer.id}`}
                        className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                        title="Edit"
                      >
                        <Edit size={16} />
                      </Link>
                      <button
                        onClick={() => handleDeleteVolunteer(volunteer.id)}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                      <button
                        onClick={() => fetchVolunteerIdCardData(volunteer.id)}
                        className="text-purple-600 hover:text-purple-900 dark:text-purple-400 dark:hover:text-purple-300"
                        title="View ID Card"
                      >
                        <CreditCard size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {filteredVolunteers.length === 0 && (
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
              {searchTerm || filterStatus !== 'all' || filterSkills ? 'No volunteers found' : 'No volunteers yet'}
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {searchTerm || filterStatus !== 'all' || filterSkills
                ? 'Try adjusting your search or filter criteria.'
                : 'Start building your volunteer team by adding your first volunteer.'
              }
            </p>
            {!searchTerm && filterStatus === 'all' && !filterSkills && (
              <div className="mt-6">
                <Link
                  to="/ngo/dashboard/volunteers/add"
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
                >
                  <Users className="mr-2" size={20} />
                  Add Volunteer
                </Link>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Volunteer Details Modal */}
      {showDetailsModal && selectedVolunteer && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Volunteer Details
                </h3>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Content */}
              <div className="space-y-4">
                {/* Basic Info */}
                <div className="flex items-center space-x-4">
                  <img
                    src={getProfilePictureUrl(selectedVolunteer.profilePicture)}
                    alt={selectedVolunteer.firstName || 'Volunteer'}
                    className="w-16 h-16 rounded-full object-cover"
                    onError={(e) => {
                      if (e.target) {
                        e.target.src = '/images/r.png';
                      }
                    }}
                  />
                  <div>
                    <h4 className="text-xl font-semibold text-gray-900 dark:text-white">
                      {selectedVolunteer.firstName && selectedVolunteer.lastName 
                        ? `${selectedVolunteer.firstName} ${selectedVolunteer.lastName}` 
                        : selectedVolunteer.firstName || selectedVolunteer.lastName || 'Unknown Name'
                      }
                    </h4>
                    <p className="text-gray-600 dark:text-gray-400">
                      Joined {selectedVolunteer.joinDate ? new Date(selectedVolunteer.joinDate).toLocaleDateString() : 'Unknown Date'}
                    </p>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedVolunteer.status || 'pending')}`}>
                      {getStatusIcon(selectedVolunteer.status || 'pending')}
                      <span className="ml-1">{selectedVolunteer.status || 'pending'}</span>
                    </span>
                    {selectedVolunteer.gender && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        👤 {selectedVolunteer.gender}
                      </p>
                    )}
                  </div>
                </div>

                {/* Contact & Location */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h5 className="font-medium text-gray-900 dark:text-white mb-2">Contact Information</h5>
                    <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                      <div className="flex items-center">
                        <Mail className="w-4 h-4 mr-2" />
                        {selectedVolunteer.email || 'No email'}
                      </div>
                      <div className="flex items-center">
                        <Phone className="w-4 h-4 mr-2" />
                        {selectedVolunteer.phone || 'No phone'}
                      </div>
                      {selectedVolunteer.dateOfBirth && (
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-2" />
                          🎂 {new Date(selectedVolunteer.dateOfBirth).toLocaleDateString()}
                        </div>
                      )}
                      {selectedVolunteer.address && (
                        <div className="flex items-center">
                          <MapPin className="w-4 h-4 mr-2" />
                          📍 {selectedVolunteer.address}
                          {selectedVolunteer.city && `, ${selectedVolunteer.city}`}
                          {selectedVolunteer.state && `, ${selectedVolunteer.state}`}
                          {selectedVolunteer.pincode && ` - ${selectedVolunteer.pincode}`}
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <h5 className="font-medium text-gray-900 dark:text-white mb-2">Emergency Contact</h5>
                    <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                      {selectedVolunteer.emergencyContact && selectedVolunteer.emergencyContact.name ? (
                        <>
                          <div>👤 {selectedVolunteer.emergencyContact.name}</div>
                          <div>🔗 {selectedVolunteer.emergencyContact.relationship || 'No relationship specified'}</div>
                          <div>📞 {selectedVolunteer.emergencyContact.phone || 'No phone'}</div>
                        </>
                      ) : (
                        <div className="text-gray-400">No emergency contact information</div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Skills & Interests */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h5 className="font-medium text-gray-900 dark:text-white mb-2">Skills</h5>
                    <div className="flex flex-wrap gap-1">
                      {selectedVolunteer.skills && Array.isArray(selectedVolunteer.skills) && selectedVolunteer.skills.length > 0 ? (
                        selectedVolunteer.skills.map((skill, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                          >
                            {skill}
                          </span>
                        ))
                      ) : (
                        <span className="text-gray-400 text-sm">No skills listed</span>
                      )}
                    </div>
                  </div>

                  <div>
                    <h5 className="font-medium text-gray-900 dark:text-white mb-2">Interests</h5>
                    <div className="flex flex-wrap gap-1">
                      {selectedVolunteer.interests && Array.isArray(selectedVolunteer.interests) && selectedVolunteer.interests.length > 0 ? (
                        selectedVolunteer.interests.map((interest, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                          >
                            {interest}
                          </span>
                        ))
                      ) : (
                        <span className="text-gray-400 text-sm">No interests listed</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Availability & Experience */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h5 className="font-medium text-gray-900 dark:text-white mb-2">Availability</h5>
                    <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                      {selectedVolunteer.availability ? (
                        <>
                          {selectedVolunteer.availability.weekdays && <div>📅 Available on weekdays</div>}
                          {selectedVolunteer.availability.weekends && <div>🌅 Available on weekends</div>}
                          {selectedVolunteer.availability.evenings && <div>🌙 Available in evenings</div>}
                          {selectedVolunteer.availability.flexible && <div>🔄 Flexible schedule</div>}
                          {!selectedVolunteer.availability.weekdays && !selectedVolunteer.availability.weekends && 
                           !selectedVolunteer.availability.evenings && !selectedVolunteer.availability.flexible && (
                            <div className="text-gray-400">No availability specified</div>
                          )}
                        </>
                      ) : (
                        <div className="text-gray-400">No availability information</div>
                      )}
                    </div>
                  </div>

                  <div>
                    <h5 className="font-medium text-gray-900 dark:text-white mb-2">Experience & Motivation</h5>
                    <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                      {selectedVolunteer.experience && (
                        <div>
                          <span className="font-medium">💼 Experience:</span>
                          <p className="mt-1">{selectedVolunteer.experience}</p>
                        </div>
                      )}
                      {selectedVolunteer.motivation && (
                        <div>
                          <span className="font-medium">💭 Motivation:</span>
                          <p className="mt-1">{selectedVolunteer.motivation}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Activity & Projects */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h5 className="font-medium text-gray-900 dark:text-white mb-2">Activity</h5>
                    <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 mr-2" />
                        Total Hours: {selectedVolunteer.totalHours || 0}
                      </div>
                      <div className="flex items-center">
                        <Star className="w-4 h-4 mr-2 text-yellow-400" />
                        Rating: {selectedVolunteer.rating || 'N/A'}
                      </div>
                      {selectedVolunteer.lastActive && (
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-2" />
                          Last Active: {new Date(selectedVolunteer.lastActive).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <h5 className="font-medium text-gray-900 dark:text-white mb-2">Projects</h5>
                    <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                      {selectedVolunteer.projects && Array.isArray(selectedVolunteer.projects) && selectedVolunteer.projects.length > 0 ? (
                        selectedVolunteer.projects.map((project, index) => (
                          <div key={index} className="flex items-center">
                            <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                            {project}
                          </div>
                        ))
                      ) : (
                        <div className="text-gray-400">No projects assigned yet</div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Status Management */}
                <div className="border-t pt-4">
                  <h5 className="font-medium text-gray-900 dark:text-white mb-2">Manage Status</h5>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleStatusChange(selectedVolunteer.id, 'active')}
                      disabled={selectedVolunteer.status === 'active'}
                      className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Activate
                    </button>
                    <button
                      onClick={() => handleStatusChange(selectedVolunteer.id, 'inactive')}
                      disabled={selectedVolunteer.status === 'inactive'}
                      className="px-3 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Deactivate
                    </button>
                    <button
                      onClick={() => handleStatusChange(selectedVolunteer.id, 'pending')}
                      disabled={selectedVolunteer.status === 'pending'}
                      className="px-3 py-1 text-xs bg-yellow-600 text-white rounded hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Mark Pending
                    </button>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                >
                  Close
                </button>
                <Link
                  to={`/ngo/dashboard/volunteers/edit/${selectedVolunteer.id}`}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Edit Volunteer
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Volunteer ID Card Modal */}
      {showIdCardModal && idCardData && (
        <VolunteerIdCard
          volunteer={idCardData.volunteer}
          ngo={idCardData.ngo}
          isOpen={showIdCardModal}
          onClose={() => setShowIdCardModal(false)}
        />
      )}
    </div>
  );
};

export default ManageVolunteers;
